/**
 * Registration Context Utilities
 *
 * Shared utilities for managing registration context data.
 * Extracted from duplicate implementations in AuthErrorHandler and AuthService.
 */

import { createLogger } from '@utils/Logger'
import { ProtectedDataStore } from '@utils/ProtectedDataStore'
import { EMkStorageKeys } from '@utils/DataStore'
import { maskEmailForMfaDisplay } from '@account/utils/mfaDisplayMask'
import { RegistrationContext } from '@account/hooks/useRegistration/_reducer'

const logger = createLogger('RegistrationContext')

/**
 * Default expiration time for registration context (24 hours)
 */
const REGISTRATION_CONTEXT_EXPIRY_HOURS = 24

/**
 * Parameters for saving registration context
 */
export interface SaveRegistrationContextParams {
  stateToken: string
  mfaFactors: any[]
  username?: string
  email?: string
  /** Okta email factor destination for MFA “code sent to…” copy; obfuscated before persistence. */
  mfaEmail?: string
  firstName?: string
  lastName?: string
  factorProfile?: any
  phone?: string
}

/**
 * Save registration context to protected storage
 *
 * @param params - Registration context parameters
 * @returns true if successful, false otherwise
 */
export const saveRegistrationContext = (params: SaveRegistrationContextParams): boolean => {
  const { stateToken, mfaFactors, username = '', email, mfaEmail, firstName = '', lastName = '', factorProfile, phone = '' } = params

  const mfaEmailForStore = mfaEmail != null && String(mfaEmail).trim() !== '' ? maskEmailForMfaDisplay(mfaEmail) : undefined

  try {
    // First check if registration context already exists (created by AuthService)
    const existingContext = ProtectedDataStore.getItem<RegistrationContext>(EMkStorageKeys.MAKO_REGISTRATION_CONTEXT, {
      store: localStorage,
    })

    if (existingContext?.stateToken === stateToken) {
      logger.debug('Registration context already exists with same stateToken')
      return true
    }

    // If no context exists or stateToken is different, create a new one
    const now = Date.now()
    const expiresAt = now + REGISTRATION_CONTEXT_EXPIRY_HOURS * 60 * 60 * 1000

    // Get the primary factor (first in array)
    const selectedFactor = mfaFactors[0]

    // Create a minimal context with required fields
    // Note: Email is optional and should NOT be stored in localStorage for security reasons
    const context: RegistrationContext = {
      stateToken,
      activationToken: '',
      registrationToken: '',
      // Email is intentionally optional - should be kept in React state (formData) only
      email, // Optional, may be undefined
      mfaEmail: mfaEmailForStore, // display-only string; obfuscated to avoid persisting full mailbox (ProtectedDataStore)
      userName: username || '',
      firstName: firstName || '',
      lastName: lastName || '',
      createdAt: now,
      expiresAt,
      mfaFactors,
      selectedFactor, // Store for MFA_VERIFY_CODE step
      mfaFactorId: selectedFactor?.id,
      factorProfile, // Store factor profile (e.g., phoneNumber for call/sms)
      phone: phone || '', // Store phone number for display in MFA verify step
      mfaRequired: true,
    }

    // Save to protected storage
    ProtectedDataStore.setItem(EMkStorageKeys.MAKO_REGISTRATION_CONTEXT, context, {
      store: localStorage,
      expirationTime: expiresAt,
    })

    return true
  } catch (error) {
    logger.error('Failed to save registration context:', error)
    return false
  }
}

/**
 * Get registration context from protected storage
 *
 * @returns Registration context or null if not found/expired
 */
export const getRegistrationContext = (): RegistrationContext | null => {
  try {
    const context = ProtectedDataStore.getItem<RegistrationContext>(EMkStorageKeys.MAKO_REGISTRATION_CONTEXT, {
      store: localStorage,
    })

    if (!context) {
      logger.debug('No registration context found')
      return null
    }

    logger.debug('Registration context retrieved successfully', {
      stateToken: context.stateToken,
      expiresAt: new Date(context.expiresAt).toISOString(),
    })
    return context
  } catch (error) {
    logger.error('Failed to get registration context:', error)
    return null
  }
}

/**
 * Check if a valid registration context exists
 *
 * @returns true if valid context exists, false otherwise
 */
export const hasValidRegistrationContext = (): boolean => {
  const context = getRegistrationContext()
  return context !== null
}

/**
 * Clear registration context from storage
 *
 * @returns true if successful, false otherwise
 */
export const clearRegistrationContext = (): boolean => {
  try {
    ProtectedDataStore.removeItem(EMkStorageKeys.MAKO_REGISTRATION_CONTEXT, {
      store: localStorage,
    })
    logger.debug('Registration context cleared successfully')
    return true
  } catch (error) {
    logger.error('Failed to clear registration context:', error)
    return false
  }
}

/**
 * Check if registration context exists for a specific state token
 *
 * @param stateToken - State token to check for
 * @returns true if context exists for this state token, false otherwise
 */
export const hasRegistrationContextForStateToken = (stateToken: string): boolean => {
  const context = getRegistrationContext()
  return context?.stateToken === stateToken
}
